// Отображение листинга: название «Листинг N. …» (в документе) + редактор кода CodeMirror 6 в рамке
// (Courier New 10 пт, как в DOCX). Подсветка и номера строк - только в редакторе. Вставка кода из IDE
// сохраняет отступы, табуляции -> 4 пробела (6.1.2); типографские кавычки из Word -> прямые (6.1.1).
import { useEffect, useRef, useState } from 'react';
import { NodeViewContent, NodeViewWrapper, type NodeViewProps } from '@tiptap/react';
import { EditorView, keymap, lineNumbers, placeholder, highlightActiveLine } from '@codemirror/view';
import { Compartment, EditorState, EditorSelection, Prec } from '@codemirror/state';
import { minimalSetup } from 'codemirror';
import { indentOnInput, bracketMatching, indentUnit } from '@codemirror/language';
import { indentLess, indentMore } from '@codemirror/commands';
import { TextSelection } from '@tiptap/pm/state';
import { readPref, writePref } from '../../../core/store';
import { ctx } from '../../../core/ctx';
import { Btn } from '../../../ui/controls';
import { Icon } from '../../../ui/icons';
import { LANGS, detectLang, fixSyntaxChars, langById, normalizeCode, oddIndentLines, syntaxIssues } from './langs';
import { codeViews } from './views';

let lineNumbersOn = readPref('tfc.lineNumbers', '1') !== '0';
const lnListeners = new Set<(v: boolean) => void>();
function setLineNumbers(v: boolean) { lineNumbersOn = v; writePref('tfc.lineNumbers', v ? '1' : '0'); lnListeners.forEach((f) => f(v)); }

/** Выйти из редактора кода в документ: после листинга (dir 1) или в его название (dir -1). */
function leave(editor: NodeViewProps['editor'], getPos: NodeViewProps['getPos'], size: () => number, dir: 1 | -1) {
  const p = typeof getPos === 'function' ? getPos() : undefined;
  if (typeof p !== 'number') return;
  const { state } = editor;
  if (dir < 0) {
    editor.view.dispatch(state.tr.setSelection(TextSelection.create(state.doc, p + 1 + (state.doc.nodeAt(p)?.firstChild?.content.size ?? 0) + 1)));
  } else {
    const after = p + size();
    const next = state.doc.nodeAt(after);
    if (!next || next.type.name !== 'paragraph') editor.view.dispatch(state.tr.insert(after, state.schema.nodes.paragraph.create()));
    editor.view.dispatch(editor.state.tr.setSelection(TextSelection.create(editor.state.doc, after + 1)).scrollIntoView());
  }
  editor.view.focus();
}

/** Курсор на первой (dir -1) / последней (dir 1) экранной строке кода (с учётом переноса длинных строк). */
function onEdgeLine(v: EditorView, head: number, dir: 1 | -1): boolean {
  const ln = v.state.doc.lineAt(head);
  if (dir < 0 ? ln.number > 1 : ln.number < v.state.doc.lines) return false;
  const a = v.coordsAtPos(head); const b = v.coordsAtPos(dir < 0 ? 0 : v.state.doc.length);
  return !a || !b || Math.abs(a.top - b.top) < 3;
}

/** Код без пустых строк в конце: writer их не выводит (в DOCX листинг кончается последней строкой кода), поэтому и в
 *  редакторе вне правки листинг кончается там же - иначе он на строку выше/ниже, чем в Word, и страницы «плывут». */
const tail = (c: string) => c.replace(/\n+$/, '');

export function ListingView({ node, updateAttributes, selected, editor, getPos }: NodeViewProps) {
  const host = useRef<HTMLDivElement>(null);
  const view = useRef<EditorView | null>(null);
  const timer = useRef<number | undefined>(undefined);
  const langC = useRef(new Compartment());
  const lnC = useRef(new Compartment());
  const nodeRef = useRef(node);
  nodeRef.current = node;
  const [ln, setLn] = useState(lineNumbersOn);
  const [focused, setFocused] = useState(false);
  const code: string = node.attrs.code || '';
  const lang: string = node.attrs.lang || '';

  const flush = () => {
    const v = view.current;
    window.clearTimeout(timer.current);
    const cur = v ? v.state.doc.toString() : '';
    const stored = nodeRef.current.attrs.code || '';
    // пустые строки в конце кода в DOCX не попадают (writer) - и на экране их нет (см. tail): это не правка
    if (v && cur !== stored && cur !== tail(stored)) updateAttributes({ code: cur });
  };

  useEffect(() => {
    const v = new EditorView({
      parent: host.current!,
      state: EditorState.create({
        doc: tail(code),
        extensions: [
          minimalSetup, EditorView.lineWrapping, indentUnit.of('    '), EditorState.tabSize.of(4), indentOnInput(), bracketMatching(),
          highlightActiveLine(),
          placeholder('Вставьте (Ctrl+V) или наберите код… Tab - отступ 4 пробела; Esc или Ctrl+Enter - выйти в текст'),
          langC.current.of(langById(lang).mode()),
          lnC.current.of(lineNumbersOn ? lineNumbers() : []),
          Prec.highest(keymap.of([
            { key: 'Tab', run: (vv) => {
              if (vv.state.selection.ranges.some((r) => !r.empty)) return indentMore(vv);
              vv.dispatch(vv.state.changeByRange((r) => {
                const col = r.head - vv.state.doc.lineAt(r.head).from;
                const n = 4 - (col % 4);
                return { changes: { from: r.head, insert: ' '.repeat(n) }, range: EditorSelection.cursor(r.head + n) };
              }));
              return true;
            } },
            { key: 'Shift-Tab', run: indentLess },
            { key: 'Escape', run: () => { flush(); leave(editor, getPos, () => nodeRef.current.nodeSize, 1); return true; } },
            { key: 'Mod-Enter', run: () => { flush(); leave(editor, getPos, () => nodeRef.current.nodeSize, 1); return true; } },
            { key: 'ArrowDown', run: (vv) => {
              const s = vv.state.selection.main;
              if (!s.empty || !onEdgeLine(vv, s.head, 1)) return false;
              flush(); leave(editor, getPos, () => nodeRef.current.nodeSize, 1); return true;
            } },
            { key: 'ArrowUp', run: (vv) => {
              const s = vv.state.selection.main;
              if (!s.empty || !onEdgeLine(vv, s.head, -1)) return false;
              flush(); leave(editor, getPos, () => nodeRef.current.nodeSize, -1); return true;
            } },
          ])),
          EditorView.domEventHandlers({
            paste: (e, vv) => {
              const t = e.clipboardData?.getData('text/plain');
              if (!t) return false;
              e.preventDefault();
              const fromWord = /urn:schemas-microsoft-com:office|class="?Mso/i.test(e.clipboardData?.getData('text/html') || '');
              let s = normalizeCode(t);
              const issues = syntaxIssues(s);
              if (fromWord || issues.quotes) { const f = fixSyntaxChars(s); if (f !== s) { s = f; ctx.toast('Типографские кавычки и тире в коде заменены на ASCII (6.1.1)', 'info', 3500); } }
              if (t.includes('\t')) ctx.toast('Табуляции заменены на 4 пробела (6.1.2)', 'info', 2500);
              vv.dispatch(vv.state.replaceSelection(s));
              if (!nodeRef.current.attrs.lang) {
                const d = detectLang(vv.state.doc.toString());
                if (d.id) { flush(); updateAttributes({ lang: d.id }); }
              }
              return true;
            },
            focus: () => { setFocused(true); return false; },
            blur: () => { setFocused(false); flush(); return false; },
          }),
          EditorView.updateListener.of((u) => {
            if (!u.docChanged) return;
            window.clearTimeout(timer.current);
            timer.current = window.setTimeout(flush, 250);
          }),
        ],
      }),
    });
    view.current = v;
    const id = node.attrs.id as string;
    if (id) codeViews.set(id, v);
    const onLn = (on: boolean) => { setLn(on); v.dispatch({ effects: lnC.current.reconfigure(on ? lineNumbers() : []) }); };
    lnListeners.add(onLn);
    return () => {
      lnListeners.delete(onLn);
      flush();
      if (id && codeViews.get(id) === v) codeViews.delete(id);
      v.destroy();
    };
  }, []);

  // id мог появиться позже (normalize) - обновить реестр
  useEffect(() => { const id = node.attrs.id; if (id && view.current) codeViews.set(id, view.current); }, [node.attrs.id]);
  // язык - без пересоздания редактора
  useEffect(() => { view.current?.dispatch({ effects: langC.current.reconfigure(langById(lang).mode()) }); }, [lang]);
  // код изменился снаружи (отмена, вставка, импорт)
  useEffect(() => {
    const v = view.current;
    if (v && !v.hasFocus && v.state.doc.toString() !== tail(code)) v.dispatch({ changes: { from: 0, to: v.state.doc.length, insert: tail(code) } });
  }, [code]);

  const nlines = code ? code.split('\n').length : 0;
  const issues = syntaxIssues(code);
  const odd = oddIndentLines(code);
  const auto = !lang && code.trim() ? detectLang(code) : null;
  const active = selected || focused;
  const fix = () => { const f = fixSyntaxChars(code); updateAttributes({ code: f }); };

  return (
    <NodeViewWrapper className={'sto-listing tfc-listing' + (active ? ' is-selected' : '')} data-type="listing">
      <NodeViewContent className="sto-listing-cap" />
      <div className="sto-listing-tools tfc-listing-tools" contentEditable={false} onMouseDown={(e) => { if ((e.target as HTMLElement).tagName !== 'SELECT') e.preventDefault(); }}>
        <select value={lang} onChange={(e) => updateAttributes({ lang: e.target.value })} title="Язык - только для подсветки в редакторе (в отчёте код без подсветки)" data-testid="listing-lang">
          {LANGS.map((l) => <option key={l.id} value={l.id}>{l.id === '' && auto?.id ? `Текст (похоже на ${langById(auto.id).title})` : l.title}</option>)}
        </select>
        <Btn icon="tfc-line-numbers" title="Номера строк (только в редакторе)" active={ln} onClick={() => setLineNumbers(!ln)} testid="listing-ln" />
        <Btn icon="tfc-code-file" title="Загрузить код из файла…" onClick={() => { const p = typeof getPos === 'function' ? getPos() : undefined; if (typeof p === 'number') editor.commands.setNodeSelection(p); ctx.run('listing.loadFile'); }} />
        <Btn icon="copy" title="Копировать код" onClick={() => { void navigator.clipboard?.writeText(code).then(() => ctx.toast('Код скопирован', 'ok', 1500)); }} />
      </div>
      <div className="sto-listing-code tfc-code" contentEditable={false} ref={host} data-testid="listing-code" />
      {(issues.quotes || issues.other.length > 0 || odd > 0 || nlines > 50) && (
        <div className="tfc-listing-warn" contentEditable={false} data-testid="listing-warn">
          {(issues.quotes || issues.other.length > 0) && (
            <span><Icon name="warn" size={13} /> В синтаксисе кода типографские символы {issues.quotes ? '(кавычки “ ”)' : issues.other.join(' ')} - нужны ASCII (6.1.1)
              <button type="button" className="tfc-link" onMouseDown={(e) => e.preventDefault()} onClick={fix} data-testid="listing-fix">Исправить</button></span>
          )}
          {odd > 0 && <span><Icon name="info" size={13} /> Отступы не кратны 2 пробелам: строк {odd} (6.1.2)</span>}
          {nlines > 50 && <span><Icon name="info" size={13} /> Объёмный листинг ({nlines} строк) - по 6.1.4 его выносят в приложение, в тексте оставляют фрагмент</span>}
        </div>
      )}
    </NodeViewWrapper>
  );
}
